import { format } from 'date-fns';
import React, { useState } from 'react';
import { ChevronLeft, Edit2, Loader, Plus } from 'react-feather';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useUserDetail } from './hooks/useUserDetail';

import CustomerDetailModal from './components/UI/CustomerDetailModal';
import EditAddressModal from './components/UI/EditAddressModal';
import ContactList from './components/UI/UserContactList';

const UserDetailPage: React.FC = () => {
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const { userDetail, isLoading, isError, error } = useUserDetail(userId || '');

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center">
        <Loader size={32} className="animate-spin text-[#3e3e3e]" />
      </div>
    );
  }

  if (isError || !userDetail) {
    alert(
      `Cannot load user details: ${error?.message || 'An unknown error occurred'}`,
    );
    return (
      <div className="flex h-[calc(100vh-56px)] items-center justify-center">
        <p className="text-lg text-[#666]">Cannot load user details.</p>
      </div>
    );
  }

  const formatCurrency = (amount: number, currency: string = 'KRW') => {
    if (currency === 'KRW') {
      return `₩${amount.toLocaleString()}`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const getTotalAmount = () => {
    const currencies = Object.entries(userDetail.stats.totalAmountByCurrency);
    if (currencies.length === 0) return '0';

    return currencies
      .map(([currency, amount]) => formatCurrency(amount, currency))
      .join(' / ');
  };

  const isAddressLimitReached = userDetail.addressList.length >= 5;

  return (
    <div className="h-[calc(100vh-56px)]">
      <div className="relative h-full overflow-y-scroll bg-[#f1f1f1] px-8 py-7 pb-10">
        <button
          type="button"
          className="mb-7 flex cursor-pointer items-center gap-3"
          onClick={handleBack}
          aria-label="Go back"
        >
          <ChevronLeft size={24} className="text-[#888]" aria-hidden="true" />
          <h2 className="text-2xl font-bold leading-8 text-[#1d1d1d]">
            {userDetail.companyName}
          </h2>
        </button>

        <div className="flex w-full justify-between gap-5">
          {/* Left Section */}
          <div className="flex w-[66%] flex-col gap-5">
            <section
              className="flex justify-between gap-4 rounded border border-[#f1f1f1] bg-white p-5 shadow-sm"
              aria-label="User summary"
            >
              <div className="w-full">
                <h4 className="mb-3 text-xs font-medium text-[#666]">
                  Amount spent
                </h4>
                <p className="text-base font-medium text-[#1d1d1d]">
                  {getTotalAmount()}
                </p>
              </div>
              <hr className="h-11 w-px border border-[#eaeaea]" />
              <div className="w-full">
                <h4 className="mb-3 text-xs font-medium text-[#666]">Orders</h4>
                <p className="text-base font-medium text-[#1d1d1d]">
                  {userDetail.stats.orderCount}
                </p>
              </div>
            </section>

            {userDetail.latestOrder && (
              <section
                className="rounded border border-[#f1f1f1] bg-white shadow-sm"
                aria-label="Last order placed"
              >
                <div className="flex items-center justify-between border-b-[1.5px] border-[#eaeaea] p-5 pb-4">
                  <h2 className="text-sm font-medium text-[#1d1d1d]">
                    Last Order Placed
                  </h2>
                  <Link
                    to={`/orders?keyword=${userDetail.email}&page=1`}
                    className="cursor-pointer rounded bg-[#3e3e3e] px-3 py-1.5 text-xs font-medium text-white no-underline hover:bg-[#222]"
                    aria-label="View all orders"
                  >
                    View all orders
                  </Link>
                </div>

                <div className="text-center">
                  <div className="p-5 text-left">
                    <Link
                      to="#"
                      className="mb-4 inline-block text-base font-bold text-[#95699f] no-underline"
                      aria-label={`Order number ${userDetail.latestOrder.orderNumber}`}
                    >
                      {userDetail.latestOrder.orderNumber}
                    </Link>
                    <div className="flex justify-between text-sm">
                      <p className="font-normal text-[#666]">
                        {format(
                          new Date(userDetail.latestOrder.createdAt),
                          'yyyy-MM-dd HH:mm:ss',
                        )}
                      </p>
                      <span className="font-medium text-[#1d1d1d]">
                        {formatCurrency(
                          userDetail.latestOrder.finalPaymentAmount,
                          userDetail.latestOrder.currencyCode,
                        )}
                      </span>
                    </div>
                  </div>

                  {userDetail.latestOrder.orderDetails.length > 0 && (
                    <div className="flex gap-4 border-t-[1.5px] border-[#eaeaea] p-5">
                      <img
                        src={
                          userDetail.latestOrder.orderDetails[0].productOption
                            .thumbnailUrlArray[0]
                        }
                        alt="product thumbnail"
                        className="h-10 w-10"
                      />
                      <div className="flex w-full flex-col">
                        <div className="flex justify-between">
                          <h4 className="text-left text-sm font-normal text-[#1d1d1d]">
                            Order Item (
                            {userDetail.latestOrder.orderDetails.length} items)
                          </h4>
                          <div className="flex gap-4 text-sm">
                            <span className="text-[#888]">
                              x{' '}
                              {userDetail.latestOrder.orderDetails.reduce(
                                (sum, detail) => sum + detail.quantity,
                                0,
                              )}
                            </span>
                            <p className="font-medium text-[#1d1d1d]">
                              {formatCurrency(
                                userDetail.latestOrder.finalPaymentAmount,
                                userDetail.latestOrder.currencyCode,
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-1 text-xs">
                          <p className="rounded bg-[#f1f1f1] px-2 py-1 text-[#222]">
                            Status: {userDetail.latestOrder.status}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            <ContactList
              contactList={userDetail.contactList}
              userUuid={userDetail.uuid}
            />
          </div>

          {/* Right Section */}
          <div className="w-[32%]">
            <section
              className="mb-5 rounded border border-[#f1f1f1] bg-white shadow-sm"
              aria-label="Customer information"
            >
              <div className="flex items-center justify-between p-5">
                <h2 className="text-base font-medium text-[#1d1d1d]">
                  Customer
                </h2>
                <button
                  type="button"
                  className="cursor-pointer bg-transparent p-0.5"
                  onClick={() => setIsCustomerModalOpen(true)}
                  aria-label="Edit customer information"
                >
                  <Edit2
                    size={16}
                    className="text-[#666666]"
                    aria-hidden="true"
                  />
                </button>
              </div>
              <div className="flex flex-col gap-2 px-5 pb-5 text-[#95699f]">
                <p className="text-sm font-normal">
                  {userDetail.businessRegistrationNumber}
                </p>
                <p className="text-sm font-normal">{userDetail.companyName}</p>
                <p className="text-sm font-normal">
                  {userDetail.representativeName}
                </p>
                <p className="text-sm font-normal">{userDetail.email}</p>
                <p className="text-sm font-normal">{userDetail.phoneNumber}</p>
                {userDetail.postalCode && (
                  <p className="text-sm font-normal">{userDetail.postalCode}</p>
                )}
                <p className="text-sm font-normal">
                  {userDetail.companyAddress}
                </p>
              </div>
            </section>

            <section
              className="mb-5 rounded border border-[#f1f1f1] bg-white shadow-sm"
              aria-label="Address list"
            >
              <div className="flex items-center justify-between p-5 pb-2">
                <h2 className="text-base font-medium text-[#1d1d1d]">
                  Address{' '}
                  {isAddressLimitReached && (
                    <span className="text-xs text-[#888]">(Max: 5)</span>
                  )}
                </h2>
                <button
                  type="button"
                  className={`cursor-pointer bg-transparent p-0.5 ${
                    isAddressLimitReached ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  onClick={() =>
                    !isAddressLimitReached && setIsAddressModalOpen(true)
                  }
                  aria-label={
                    isAddressLimitReached
                      ? 'Address limit reached'
                      : 'Add address'
                  }
                  disabled={isAddressLimitReached}
                >
                  <Plus
                    size={16}
                    className={`${
                      isAddressLimitReached ? 'text-[#ccc]' : 'text-[#666666]'
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>
              <div className="address-list">
                {userDetail.addressList.length > 0 ? (
                  userDetail.addressList.map((address) => (
                    <div
                      key={address.addressUuid}
                      className="cursor-pointer p-5 text-sm font-normal leading-[140%] text-[#222] hover:bg-[#f6f6f6]"
                      tabIndex={0}
                      role="button"
                      aria-label="View address details"
                      onClick={() => setIsAddressModalOpen(true)}
                    >
                      {address.postalCode && `${address.postalCode} `}
                      {address.province && `${address.province} `}
                      {address.city && `${address.city} `}
                      {address.addressDetail}
                      {address.isDefault && (
                        <span className="ml-2 rounded bg-[#3e3e3e] px-2 py-1 text-xs text-white">
                          Default
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-5 text-center text-sm text-[#666]">
                    No addresses found
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Customer Modal */}
      {isCustomerModalOpen && (
        <CustomerDetailModal
          onClose={() => setIsCustomerModalOpen(false)}
          userDetail={userDetail}
        />
      )}

      {/* Address Modal */}
      {isAddressModalOpen && (
        <EditAddressModal
          onClose={() => setIsAddressModalOpen(false)}
          addressList={userDetail.addressList}
          userUuid={userDetail.uuid}
        />
      )}
    </div>
  );
};

export default UserDetailPage;
